<template>
  <LoadingPage v-if="initializing || !CONFIG"/>
  <div v-else-if="CONFIG.maintenanceMode == MaintenanceMode.CLOSED" class="fullscreenCenteredContainer">
    <img src="./assets/images/minicity_logo.png" class="mb-3"/>
    <div class="notification bulmaNotification is-danger">{{ CONFIG.maintenanceMessage }}</div>
  </div>
  <router-view v-else />
</template>

<script lang="ts" setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { tryToResumeSession, chromeless } from "./core/session";
import { loadConfig, CONFIG, MaintenanceMode } from "./core/config";
import router from "./router";

const initializing = ref<boolean>(true);

const route = useRoute();

onMounted(async () => {

  loadConfig();

  await useRouter().isReady();

  // The portal can be launched "chromeless" - e.g. when embedded within the tablet app
  // Store that state, so the rest of the app can refer to it
  if (route.query.chromeless?.toString() == "true") {
    chromeless.value = true;
  }

  // When embedded within the tablet app, we can inherit its session
  if (route.query.sessionId) {
    // Remove sessionId URL query param, so if the user shares a link, their session won't be leaked
    const url = new URL(window.location.toString());
    url.searchParams.delete('sessionId');    
    window.history.replaceState({}, '', url);

    // Try to resume indicated session
    tryToResumeSession(route.query.sessionId.toString())
      .catch((err) => {
        console.error("sessionId parameter was not valid - need to login");
        router.push(`/login?forwardTo=${encodeURIComponent(document.location.pathname + document.location.search)}`);
      })
      .finally(() => {
        initializing.value = false;
      });
  } else {
    // Try to resume cached session, if any
    tryToResumeSession()
      .catch(err => {
        // Swallow session-resume errors - it's not unusual to encounter e.g. an expired session
        console.log("Failed to resume session");
      })
      .finally(() => {
        initializing.value = false;
      });
  }
});

</script>

<style lang="scss">
@import "./assets/variables.scss";

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  height: 100%;
}

.ctaButton {
  color: white;
  font-weight: 700;
  font-size: 100%;
  line-height: inherit;

  background-color: $primary;
  padding: 0.35rem 1.5rem 0.375rem 1.5rem;
  border-style: none;
  border-radius: 0.25rem;

  cursor: pointer;
}
</style>
